/**
 * Live progress card for an in-flight summarize job — stage, chunk progress,
 * streamed character count, elapsed timer, cancel. Renders at the top of the
 * message log while the server-side job runs; the chat stays fully usable.
 */
import { useEffect, useState } from 'react';
import { Button } from '@onestone/ui/button';
import { chatStore, type SummarizeJob } from '@/lib/chatStore';

function stageLabel(job: SummarizeJob): string {
  switch (job.stage) {
    case 'preparing':
      return 'Preparing…';
    case 'summarizing': {
      const part = job.totalChunks > 1 ? ` — part ${job.chunk}/${job.totalChunks}` : '';
      const chars = job.chars > 0 ? ` · ${(job.chars / 1000).toFixed(1)}K chars` : '';
      return `Summarizing${part}${chars}`;
    }
    case 'saving':
      return 'Saving checkpoint…';
  }
}

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
}

export function SummarizeProgressCard({ job }: { job: SummarizeJob }) {
  const [now, setNow] = useState(() => Date.now());
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const elapsed = now - new Date(job.startedAt).getTime();

  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3 shadow-[inset_0_1px_0_0_rgb(255_255_255/0.04)]">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-emerald-500 motion-reduce:animate-none"
            aria-hidden="true"
          />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Summarizing</span>
        </div>
        <span className="text-xs tabular-nums text-muted-foreground/80">{formatElapsed(elapsed)}</span>
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span className="text-sm text-foreground/90" role="status" aria-live="polite">
          {cancelling ? 'Cancelling…' : stageLabel(job)}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={cancelling}
          onClick={() => {
            setCancelling(true);
            chatStore.cancelSummarize();
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
